import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ProfileFieldInput } from "../../components/ProfileFieldInput";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";
import { GROUP_LABELS, groupFields } from "../../lib/fieldMeta";

const EMPTY = { name: "", description: "", role: "employee", theme: "inception-black", title_en: "", title_ar: "", profile: {} };

const Text = ({ id, label, value, onChange, dir = "ltr", required }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}{required && <span className="text-wade"> *</span>}</Label>
    <Input id={id} dir={dir} value={value} required={required} onChange={(e) => onChange(e.target.value)}
      className={`h-12 rounded-xl ${dir === "rtl" ? "font-arabic text-right" : ""}`} data-testid={`template-form-${id}`} />
  </div>
);

const Swatches = ({ themes, value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="template-form-theme">
    {Object.entries(themes).map(([key, th]) => (
      <button key={key} type="button" title={th.name} onClick={() => onChange(key)} data-testid={`template-theme-${key}`}
        className={`flex h-10 w-10 items-center justify-center rounded-xl border ${value === key ? "ring-2 ring-slate-900 ring-offset-2" : "border-slate-200"}`}
        style={{ backgroundColor: th.bg, color: th.fg }}>
        {value === key && <Check className="h-4 w-4" />}
      </button>
    ))}
  </div>
);

export const TemplateDialog = ({ open, onOpenChange, template, meta, themes, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { t, lang, pick } = useLang();
  const enabled = meta.fields.filter((f) => f.enabled);
  const roles = meta.roles.filter((r) => r.key !== "admin" || user.is_owner || form.role === "admin");
  useEffect(() => { if (open) setForm(template ? { ...EMPTY, ...template } : EMPTY); }, [open, template]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setField = (k, v) => setForm((f) => ({ ...f, profile: { ...f.profile, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const profile = Object.fromEntries(enabled.map((f) => [f.key, form.profile[f.key] || ""]).filter(([, v]) => v));
    const { id, ...rest } = form;
    try {
      if (template) await api.put(`/admin/templates/${template.id}`, { ...rest, profile });
      else await api.post("/admin/templates", { ...rest, profile });
      toast.success(t(template ? "Template updated" : "Template created"));
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="template-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display text-xl">{template ? t("Edit {name}", { name: template.name }) : t("Add template")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Text id="name" label={t("Template name")} value={form.name} onChange={set("name")} required />
            <Text id="description" label={t("Description")} value={form.description} onChange={set("description")} />
            <Text id="title_en" label={t("Title (English)")} value={form.title_en} onChange={set("title_en")} />
            <Text id="title_ar" label={t("Title (Arabic)")} value={form.title_ar} onChange={set("title_ar")} dir="rtl" />
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Role")}</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="template-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.key} value={r.key} data-testid={`template-form-role-${r.key}`}>{pick(r.name_en, r.name_ar)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("Card colour")}</Label>
              <Swatches themes={themes} value={form.theme} onChange={set("theme")} />
            </div>
          </div>
          {groupFields(enabled).map(([group, list]) => (
            <fieldset key={group} className="border-t border-slate-100 pt-5">
              <legend className="mb-3 font-display text-sm font-bold text-slate-900">{GROUP_LABELS[group][lang]}</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {list.map((f) => (
                  <ProfileFieldInput key={f.key} field={{ ...f, required: false }} value={form.profile[f.key]} lang={lang}
                    testPrefix="template-form" onChange={setField} />
                ))}
              </div>
            </fieldset>
          ))}
          <button type="submit" disabled={saving} data-testid="template-form-submit"
            className="btn-press min-h-[50px] w-full rounded-2xl bg-cobalt px-6 font-semibold text-white hover:bg-cobalt-hover disabled:opacity-60">
            {saving ? t("Saving…") : t(template ? "Save template" : "Create template")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
